/** @jsx jsx */
import { useEffect, useState } from 'react';
import { css, jsx } from '@emotion/react';

import { Img } from '../../display/Img';
import { Animation, Colors, Typography } from '../../../theme';
import { labelStyles } from '@zapier/style-encapsulation';

export type Props = {
  /**
   * Controls whether `Avatar` is hidden from screenreaders.
   * Useful when it renders alongside redundant content.
   */
  ariaHidden?: boolean;
  /**
   * Background color of the Avatar when no image exists.
   * When omitted, will default to `lava`, `ocean`, or `sun`
   * based on the child number it is.
   * V5 colors are `primary`, `secondary` and `tertiary`
   * V4 colors will automatically map to V5
   */
  color?: 'lava' | 'ocean' | 'sun' | 'primary' | 'secondary' | 'tertiary';
  /** Indicates whether to render the component as a block-level element. */
  isBlock?: boolean;

  /** Indicates whether the Avatar is interactive, i.e. that it lives within an interactive node. */
  isInteractive?: boolean;

  /** Indicates whether the Avatar is selected / clicked on. */
  isSelected?: boolean;

  /** Name of user */
  name: string;

  /** Whether or not to lazy load the underlying image. */
  shouldLazilyLoad?: boolean;
  /** Size of the `Avatar` to display
   *  Note: Size 18 is deprecated as of v5.0.0
   */
  size?: 48 | 40 | 25 | 18;

  /** URL to image */
  url?: string | null;
};

const getNameInitials = (name: string): string => {
  const initials = (name || '')
    .trim()
    .split(/\s+/)
    .map((s) => s[0]);
  const firstLetter = initials[0];
  const lastLetter = initials[initials.length - 1];

  if (!firstLetter) {
    return '';
  }

  return initials.length === 1
    ? String(firstLetter)
    : `${firstLetter}${lastLetter}`;
};

const mapV4ColortoV5Color = (color: Props['color']) => {
  let transformedColor;
  switch (color) {
    case 'lava':
      transformedColor = 'primary';
      break;
    case 'ocean':
      transformedColor = 'secondary';
      break;
    case 'sun':
      transformedColor = 'tertiary';
      break;
    default:
      transformedColor = color;
  }
  return transformedColor;
};

const Styles = labelStyles('Avatar', {
  root: (hasError: boolean, props: Pick<Props, 'url' | 'size'>) => [
    css`
      ${Typography.base};
      font-weight: 700;
      line-height: 1;
      user-select: none;
      align-items: center;
      border-radius: 50%;
      display: inline-flex;
      justify-content: center;
      overflow: hidden;
      position: relative;
      border: 1px solid ${Colors.neutral100};
      transition: ${Animation.transitionValue};

      &[data-block] {
        display: flex;
      }

      &[data-interactive] {
        &:hover,
        &:focus {
          border-color: ${Colors.neutral300};
        }
      }

      &[data-selected][data-selected] {
        /* Repurposes --zds-radius-none so that no border is rendered on the sizes 18 and 25 (i.e. small) on PDL  */
        border: var(--zds-radius-none, 1px solid ${Colors.blue});

        &[data-size='40'],
        &[data-size='48'] {
          border: var(--zds-border-weak, 1px solid ${Colors.blue});
          border-width: var(--zds-border-width-default, 2px);
        }

        /* Outline should only be rendered on PDL */
        outline: var(--zds-border-focus, none);
        outline-offset: 2px;
      }
    `,
    !props.url || (props.url && hasError)
      ? css`
          &:nth-of-type(1n),
          &[data-color='lava'][data-color] {
            background-color: ${Colors.neutral800};
            color: ${Colors.neutral100};
          }
          &:nth-of-type(1n),
          &[data-color='primary'][data-color] {
            background-color: ${Colors.neutral800};
            color: ${Colors.neutral100};
          }

          &:nth-of-type(2n),
          &[data-color='ocean'][data-color] {
            background-color: ${Colors.night};
            color: ${Colors.neutral100};
          }
          &:nth-of-type(2n),
          &[data-color='secondary'][data-color] {
            background-color: ${Colors.night};
            color: ${Colors.neutral100};
          }

          &:nth-of-type(3n),
          &[data-color='sun'][data-color] {
            background-color: ${Colors.moss};
            color: ${Colors.neutral100};
          }
          &:nth-of-type(3n),
          &[data-color='tertiary'][data-color] {
            background-color: ${Colors.moss};
            color: ${Colors.neutral100};
          }
        `
      : null,
    css`
      /* TODO: Use defined font sizes for v8 */
      font-size: ${Math.floor(
        props.size! * (props.size! > 25 ? 0.35 : 0.45)
      )}px;

      &[data-size='18'] {
        height: 18px;
        width: 18px;
      }

      &[data-size='25'] {
        height: var(--zds-size-small, 25px);
        width: var(--zds-size-small, 25px);
      }

      &[data-size='40'] {
        height: var(--zds-size-medium, 40px);
        width: var(--zds-size-medium, 40px);
        border: 2px solid var(--zds-stroke-weaker, ${Colors.neutral100});
      }

      &[data-size='48'] {
        height: var(--zds-size-large, 48px);
        width: var(--zds-size-large, 48px);
        border: 2px solid var(--zds-stroke-weaker, ${Colors.neutral100});
      }
    `,
    props.size! >= 40 &&
      css`
        border-width: 2px;
      `,
  ],
  img: css`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  `,
});
/**
 * Displays a user's avatar. Falls back to their initials if an invalid
 * image url or no image url is provided.
 */

export const Avatar = ({
  ariaHidden,
  color,
  isBlock,
  isInteractive,
  isSelected,
  name,
  url,
  shouldLazilyLoad = true,
  size = 25,
}: Props) => {
  // Used to fall back to initials if the image fails to load
  const [hasError, setHasError] = useState(false);
  const initials = name ? getNameInitials(name) : null; // reset hasError if the url changes

  useEffect(() => {
    if (hasError) {
      setHasError(false);
    } // we don't want hasError to trigger this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return (
    <div
      aria-hidden={ariaHidden}
      aria-label={name}
      css={Styles.root(hasError, { url, size })}
      data-block={isBlock ? 'true' : undefined}
      data-color={mapV4ColortoV5Color(color)}
      data-interactive={isInteractive ? 'true' : undefined}
      data-selected={isSelected ? 'true' : undefined}
      data-size={size}
      data-testid="Avatar"
      data-zds
      role="img"
    >
      {url && !hasError ? (
        <Img
          css={Styles.img}
          alt={name}
          ariaHidden={true}
          height={size} // Use `undefined` for default prop, `null` to opt out.
          onError={() => setHasError(true)}
          src={url}
          width={size}
          loading={shouldLazilyLoad ? 'lazy' : 'eager'}
        />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
};
